/**
 * 駒コンポーネント
 *
 * ごいたの駒を表示・選択可能にする
 */

import React from 'react';
import type { Piece as PieceType } from '@/types/game';
import { PIECE_NAMES, PIECE_COLORS } from '@utils/constants';

/**
 * 駒コンポーネントProps
 */
interface PieceProps {
  piece: PieceType;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** 駒の向き（縦長 or 横長） */
  orientation?: 'vertical' | 'horizontal';
  /** 現在の攻め駒かどうか */
  isCurrentAttack?: boolean;
  /** 駒のサイズ（normal: 通常、large: 大きい） */
  size?: 'normal' | 'large';
}

// 駒の基本サイズ
const PIECE_WIDTH = 60;
const PIECE_HEIGHT = 80;
const PIECE_WIDTH_LARGE = 100;
const PIECE_HEIGHT_LARGE = 130;

/**
 * 駒コンポーネント
 */
export const Piece: React.FC<PieceProps> = ({ piece, selected = false, disabled = false, onClick, orientation = 'vertical', isCurrentAttack = false, size = 'normal' }) => {
  const { type, visible } = piece;

  // サイズに応じた基本寸法を選択
  const baseWidth = size === 'large' ? PIECE_WIDTH_LARGE : PIECE_WIDTH;
  const baseHeight = size === 'large' ? PIECE_HEIGHT_LARGE : PIECE_HEIGHT;

  // 駒のサイズ（縦長 or 横長）
  const pieceWidth = orientation === 'horizontal' ? baseHeight : baseWidth;  // 横長: 高さと幅を入れ替え
  const pieceHeight = orientation === 'horizontal' ? baseWidth : baseHeight;

  const baseSizeStyle = {
    width: `${pieceWidth}px`,
    height: `${pieceHeight}px`,
    borderRadius: '6px',
  };

  // 伏せ駒の場合は背面表示
  if (!visible) {
    const hiddenSelectedClasses = selected ? 'ring-4 ring-blue-400 scale-110' : '';
    const hiddenInteractionClasses = !disabled ? 'cursor-pointer hover:scale-105' : 'opacity-50';

    return (
      <div
        className={`
          bg-goita-back border-2 border-gray-600
          flex items-center justify-center
          ${hiddenInteractionClasses}
          ${hiddenSelectedClasses}
          transition-all duration-200
        `}
        style={baseSizeStyle}
        onClick={!disabled ? onClick : undefined}
      >
        <span className="text-white text-sm">伏</span>
      </div>
    );
  }

  // 表面の駒
  const colorClass = PIECE_COLORS[type];
  const name = PIECE_NAMES[type];

  // 選択時のクラス名を構築
  const selectedClasses = selected ? 'ring-4 ring-blue-400 scale-110' : '';
  const interactionClasses = !disabled ? 'cursor-pointer hover:scale-105' : 'opacity-50 cursor-not-allowed';
  // 現在の攻め駒の場合は赤枠を表示（太めの8px）
  const currentAttackClasses = isCurrentAttack ? 'ring-8 ring-red-500' : '';

  return (
    <div
      className={`
        ${colorClass} border-2 border-gray-800
        flex items-center justify-center
        ${interactionClasses}
        ${selectedClasses}
        ${currentAttackClasses}
        transition-all duration-200
        shadow-lg
      `}
      style={baseSizeStyle}
      onClick={!disabled ? onClick : undefined}
    >
      <span className="text-white font-bold text-3xl">{name}</span>
    </div>
  );
};
